import { useCallback, useEffect, useRef, useState } from "react";
import * as React from "react";
import { Form, Radio, Input, Row, Col } from "antd";
import { UploadOutlined } from "@ant-design/icons";

import IconButton from "../../ui/IconButton";
import FormHeader from "../FormHeader";
import { CompanyIdentificationValue, CompanyIdentificationType, EquityPlaceholderName } from "../../../types/newLinkBuilder";
import TagsInputField from "../../TagsInputField";

interface CompanyIdentificationFormProps {
    value: CompanyIdentificationValue;
    setValue: (value: CompanyIdentificationValue | ((prev: CompanyIdentificationValue) => CompanyIdentificationValue)) => void;
    handlePlaceholder: (name: EquityPlaceholderName) => void;
}

const CompanyIdentificationForm: React.FC<CompanyIdentificationFormProps> = ({ value, setValue, handlePlaceholder }) => {
    const handleCompanyIdentificationChange = useCallback((field: keyof CompanyIdentificationValue, newValue: string) => {
        if (field === 'searchInput') {
            const company = JSON.parse(newValue);
            const parsedSymbol = company.symbol || newValue;
            setValue((prev) => ({
                ...prev,
                searchInput: parsedSymbol,
            }));
        } else {
            setValue((prev) => ({
                ...prev,
                [field]: newValue,
            }));
        }
    }, [setValue])

    const handleRadioChange = useCallback((e: any) => {
        setValue((prev) => ({
            ...prev,
            selected: e.target.value,
        }))
    }, [value, setValue])

    const searchInputRef = useRef<any>(null)
    useEffect(() => {
        searchInputRef.current?.focus()
    }, [])

    return (
        <div>
            <FormHeader title="Company Identification" />
            <Form.Item name="companyIdentification">
                    <Radio.Group 
                        value={value.selected}
                        onChange={handleRadioChange}
                        style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}
                    >
                        {/* Search Section */}
                        <Row align="middle">
                            <Col span={6} style={{ display: 'flex', justifyContent: 'start' }}>
                                <Radio name="companyIdentification" value={CompanyIdentificationType.Search}>Search</Radio>
                            </Col>
                            <Col span={18} style={{ display: 'flex', justifyContent: 'start' }}>
                                <TagsInputField
                                    disabled={value.selected === CompanyIdentificationType.CellReference}
                                    placeholder="Search for a company"
                                    onChange={(value) => handleCompanyIdentificationChange("searchInput", value)}
                                />
                            </Col>
                        </Row>

                        {/* Cell Reference Section */}
                        <Row align="middle">
                            <Col span={6} style={{ display: 'flex', justifyContent: 'start' }}>
                                <Radio name="companyIdentification" value={CompanyIdentificationType.CellReference}>Cell Reference</Radio>
                            </Col>
                            <Col span={18} style={{ display: 'flex', justifyContent: 'start', flexDirection: 'column', gap: '10px' }}>
                                <Row>
                                    <Col span={6} style={{ display: 'flex', justifyContent: 'start' }}>
                                        <span>- Exchange</span>
                                    </Col>
                                    <Col span={16} style={{ display: 'flex', justifyContent: 'start' }}>
                                        <Input
                                            placeholder="Exchange"
                                            disabled={value.selected === CompanyIdentificationType.Search}
                                            value={value.exchange}
                                            onChange={(e) => handleCompanyIdentificationChange("exchange", e.target.value)}
                                        />
                                    </Col>
                                    <Col span={2} style={{ display: 'flex', justifyContent: 'end' }}>
                                        <IconButton
                                            disabled={value.selected === CompanyIdentificationType.Search}
                                            icon={<UploadOutlined />}
                                            onClick={() => handlePlaceholder(EquityPlaceholderName.CompanyIdentificationExchangeCellReference)}
                                        />
                                    </Col>
                                </Row>

                                <Row>
                                    <Col span={6} style={{ display: 'flex', justifyContent: 'start' }}>
                                        <span>- Ticker</span>
                                    </Col>
                                    <Col span={16} style={{ display: 'flex', justifyContent: 'start' }}>
                                        <Input
                                            placeholder="Ticker"
                                            disabled={value.selected === CompanyIdentificationType.Search}
                                            value={value.ticker}
                                            onChange={(e) => handleCompanyIdentificationChange("ticker", e.target.value)}
                                        />
                                    </Col>
                                    <Col span={2} style={{ display: 'flex', justifyContent: 'end' }}>
                                        <IconButton
                                            disabled={value.selected === CompanyIdentificationType.Search}
                                            icon={<UploadOutlined />}
                                            onClick={() => handlePlaceholder(EquityPlaceholderName.CompanyIdentificationTickerCellReference)}
                                        />
                                    </Col>
                                </Row>
                            </Col>
                        </Row>
                    </Radio.Group>
            </Form.Item>
        </div>
    )
}

export default CompanyIdentificationForm; 